import { atom } from 'nanostores';

/**
 * The file currently being written by the model, streamed token-by-token.
 *
 * This is intentionally separate from `editorStore`/`FilesStore` (which are
 * populated by the sandbox file-watch *after* a write completes). It holds the
 * live, partial `content` from the in-flight `write_file` tool call so the code
 * viewer can animate the file being typed out. `null` when nothing is streaming.
 */
export interface StreamingFile {
  filePath: string;
  content: string;
  /**
   * Lifecycle phase. A finished file is kept (`done`/`error`) rather than cleared
   * so the viewer can keep showing it — with the typing animation stopped — until
   * the sandbox file-watch populates the canonical document or a new write starts.
   */
  status: 'streaming' | 'done' | 'error';
  /** Populated when `status === 'error'` (the `write_file` tool reported a failure). */
  errorText?: string;
}

export const streamingFileStore = atom<StreamingFile | null>(null);

/**
 * Authoritative update of the streaming file. The caller (the chat render effect)
 * is the single source of truth for `status` — it knows both the tool's terminal
 * state and whether the chat is actually generating — so this setter does no
 * status arbitration of its own. In particular it MUST be able to flip a file
 * back to `streaming` (e.g. a resumable stream reconnecting after a reload briefly
 * looked finished), so there is deliberately no "don't resurrect" guard here.
 */
export function updateStreamingFile(
  filePath: string,
  content: string,
  status: StreamingFile['status'],
  errorText?: string,
) {
  streamingFileStore.set({ filePath, content, status, errorText });
}

export function clearStreamingFile() {
  streamingFileStore.set(null);
}

/**
 * The file an in-flight `edit_file` call is modifying (full WORK_DIR path).
 * Indicator-only: edit input is diff-shaped, so there is no content to stream
 * — the viewer shows canonical content with an "editing" spinner instead.
 */
export const editingFileStore = atom<string | null>(null);

export function setEditingFile(path: string | null) {
  if (editingFileStore.get() !== path) {
    editingFileStore.set(path);
  }
}
